import React, { FC } from 'react';
import { Chip as BaseChip, ChipProps, Stack, IconButton } from '@mui/material';
import { Theme } from '@mui/material/styles';

import { Graph24 } from '../../icons/Graph24';
import { Close24 } from '../../icons/Close24';
import { Typography } from '../Typography';
import { Counter } from '../Counter';


type ChipVariant = 'base' | 'primary' | 'secondary';

interface ChipCustom extends Omit<ChipProps, 'variant'> {
    variant?: ChipVariant;
    active?: boolean;
    graphIcon?: boolean;
    counter?: number;
    onCloseChip?: () => void;
    label?: React.ReactNode;
    disabled?: boolean;
}

const ChipContent: FC<ChipCustom> = ({
    label,
    graphIcon,
    counter,
    onCloseChip,
    active,
    variant,
    disabled,
}) => (
    <Stack direction='row' spacing={1} alignItems='center'>
        {graphIcon && <Graph24 fontSize='small' />}
        <Typography variant='mediumCaption1'>{label}</Typography>
        {counter ? (
            <Counter
                count={counter}
                variant={disabled ? 'black' : 'primary'}
                gray={disabled}
                sx={disabled && { backgroundColor: 'grayscale.shade40', color: 'grayscale.shade85' }}
            />
        ) : onCloseChip ? (
            <IconButton onClick={onCloseChip} size='small'>
                <Close24
                    fontSize='small'
                    sx={{
                        color: active && variant === 'secondary'
                            ? 'grayscale.shade0'
                            : 'grayscale.shade85',
                    }}
                />
            </IconButton>
        ) : null}
    </Stack>
);

const getChipStyles = (grayscale: Theme['palette']['grayscale'], variant: ChipVariant, active?: boolean) => {
    let backgroundColor = grayscale.shade10;
    let color = grayscale.shade95;
    let border = 'none';

    if (variant === 'base')
        backgroundColor = grayscale.shade0;
    else if (variant === 'secondary' && active) {
        backgroundColor = grayscale.shade95;
        color = grayscale.shade0;
    }

    if (active && (variant === 'base' || variant === 'primary'))
        border = `1px solid ${grayscale.shade95}`;


    return {
        px: 3,
        py: 1.5,
        backgroundColor,
        color,
        border,
        '&:hover': !active && {
            backgroundColor: grayscale.shade20,
            color: grayscale.shade95,
        },
        '&:active': {
            backgroundColor,
            color,
        },
        '&.Mui-disabled': {
            opacity: 1,
            color: grayscale.shade85,
            backgroundColor: grayscale.shade20,
        },
    };
};

export const Chip: FC<ChipCustom> = ({ sx, variant = 'base', ...props }) => (
    <BaseChip
        {...props}
        variant='filled'
        disabled={props.disabled}
        label={<ChipContent {...props} />}
        sx={[
            theme => getChipStyles(theme.palette.grayscale, variant, props.active),
            ...Array.isArray(sx) ? sx : [sx],
        ]}
    />
);
